import { Injectable } from '@angular/core';
import { AdvancedSearchOption, AdvancedSearchOptions, InstanceService } from '@tylertech/cj-inmate-search-angular-sdk';
import { Observable, catchError, map, of, shareReplay } from 'rxjs';

export type { AdvancedSearchOption, AdvancedSearchOptions };

// An empty options structure with every filter key present, used as the safe
// default when the API errors so consumers can always render (just "Any").
export const EMPTY_ADVANCED_SEARCH_OPTIONS: Required<AdvancedSearchOptions> = {
  offenderType: [],
  supervisionStatus: [],
  locationType: [],
  facility: [],
  countyOfSupervision: [],
  convictionCounty: [],
  race: []
};

export type AdvancedSearchOptionLabels = Record<string, Record<string, string>>;

export function toOptionLabels(options: AdvancedSearchOptions): AdvancedSearchOptionLabels {
  const labels: AdvancedSearchOptionLabels = {};

  for (const [field, list] of Object.entries(options ?? {})) {
    if (!Array.isArray(list)) {
      continue;
    }
    const forField: Record<string, string> = {};
    for (const option of list as AdvancedSearchOption[]) {
      if (option?.value != null && option.label) {
        forField[String(option.value)] = option.label;
      }
    }
    labels[field] = forField;
  }

  return labels;
}

@Injectable({
  providedIn: 'root'
})
export class AdvancedSearchOptionsService {
  private readonly _cache = new Map<string, Observable<AdvancedSearchOptions>>();

  constructor(private _instanceService: InstanceService) { }

  getAdvancedSearchOptions(instanceId: string): Observable<AdvancedSearchOptions> {
    const cached = this._cache.get(instanceId);
    if (cached) {
      return cached;
    }

    const request = this._instanceService.getAdvancedSearchOptions(instanceId).pipe(
      catchError(error => {
        console.error('Error fetching advanced search options:', error);
        this._cache.delete(instanceId);
        return of({ ...EMPTY_ADVANCED_SEARCH_OPTIONS });
      }),
      shareReplay({ bufferSize: 1, refCount: false })
    );

    this._cache.set(instanceId, request);
    return request;
  }

  getOptionLabels(instanceId: string): Observable<AdvancedSearchOptionLabels> {
    return this.getAdvancedSearchOptions(instanceId).pipe(map(toOptionLabels));
  }
}